import React from 'react';
import { createPortal } from 'react-dom';
import { FmButton } from './FmButton';
import { FmOverlayPortalContext } from './FmOverlayPortalContext';

export interface FmModalProps {
  open: boolean;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Primary confirm action. When omitted, no confirm button is shown. */
  onOk?: () => void;
  okText?: React.ReactNode;
  okDisabled?: boolean;
  okLoading?: boolean;
  onCancel: () => void;
  cancelText?: React.ReactNode;
  /** Replace the default ok/cancel footer entirely. Pass `null` to hide it. */
  footer?: React.ReactNode | null;
  /**
   * When false, hide passive dismiss controls and ignore backdrop/Escape.
   * Use for alert/confirm dialogs where the user must acknowledge the action.
   */
  dismissible?: boolean;
  /**
   * Semantic weight of the confirm action. `destructive` paints the confirm in
   * the danger fill and promotes the cancel to a bordered secondary, so the
   * safe choice reads as the default one. Never express a destructive
   * confirmation with the brand primary.
   */
  tone?: 'default' | 'destructive';
  /**
   * `overlay` (default) portals the dialog over the page with a scrim, focus
   * trap and body scroll lock. `inline` renders the same dialog surface in
   * normal flow with no scrim, trap or lock — for gallery specimens where two
   * dialog surfaces must be visible side by side and compared. Never use
   * `inline` for a real confirmation.
   */
  presentation?: 'overlay' | 'inline';
  /**
   * Semantic dialog width. Prefer this over a raw `width` in page code so route
   * code requests a size, not a pixel value. An explicit `width` still
   * overrides.
   */
  size?: 'sm' | 'md' | 'lg';
  width?: number | string;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const MODAL_WIDTH_BY_SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'var(--fm-modal-w-sm)',
  md: 'var(--fm-modal-w-md)',
  lg: 'var(--fm-modal-w-lg)',
};

function resolveModalWidth(width: number | string | undefined, size: 'sm' | 'md' | 'lg'): string {
  if (width === undefined) return MODAL_WIDTH_BY_SIZE[size];
  return typeof width === 'number' ? `${width}px` : width;
}

interface FmModalDefaultFooterProps {
  onOk?: () => void;
  okText: React.ReactNode;
  okDisabled: boolean;
  okLoading: boolean;
  onCancel: () => void;
  cancelText: React.ReactNode;
  tone: 'default' | 'destructive';
}

const FmModalDefaultFooter: React.FC<FmModalDefaultFooterProps> = ({
  onOk,
  okText,
  okDisabled,
  okLoading,
  onCancel,
  cancelText,
  tone,
}) => {
  const destructive = tone === 'destructive';
  return (
    <>
      {/* Cancel is first in DOM order, so it is also the first focusable element
          of a non-dismissible destructive dialog: the safe choice takes initial
          focus. */}
      <FmButton
        variant={destructive ? 'bordered' : 'default'}
        onClick={onCancel}
        data-testid="fm-modal-cancel"
      >
        {cancelText}
      </FmButton>
      {onOk ? (
        <FmButton
          variant="primary"
          danger={destructive}
          onClick={onOk}
          disabled={okDisabled || okLoading}
          data-testid="fm-modal-ok"
        >
          {okText}
        </FmButton>
      ) : null}
    </>
  );
};

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const EXTERNAL_OVERLAY_SELECTOR = [
  '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  '.ant-dropdown:not(.ant-dropdown-hidden)',
  '.ant-popover:not(.ant-popover-hidden)',
].join(',');

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true',
  );
}

function hasVisibleChildOverlay(modalRoot: HTMLElement | null): boolean {
  if (!modalRoot) return false;
  return Array.from(modalRoot.querySelectorAll<HTMLElement>(EXTERNAL_OVERLAY_SELECTOR)).some(
    (overlay) => overlay.getClientRects().length > 0,
  );
}

/**
 * The attributes that separate a real dialog from an inline specimen of one, and
 * a destructive tone from a benign one — resolved once so the render reads as
 * markup rather than as a chain of conditionals.
 */
function dialogSurfaceAttributes(args: {
  inline: boolean;
  tone: 'default' | 'destructive';
  title: React.ReactNode;
  titleId: string;
}) {
  const { inline, tone, title, titleId } = args;
  return {
    rootClassName: cx('fm-modal__root', inline && 'fm-modal__root--inline'),
    role: inline ? 'group' : 'dialog',
    ariaModal: inline ? undefined : true,
    dataTone: tone === 'destructive' ? 'destructive' : undefined,
    ariaLabelledBy: title ? titleId : undefined,
  } as const;
}

const FmModalBackdrop: React.FC<{
  inline: boolean;
  dismissible: boolean;
  onCancel: () => void;
}> = ({ inline, dismissible, onCancel }) => {
  if (inline) return null;
  return (
    <div
      className="fm-modal__backdrop"
      onClick={dismissible ? onCancel : undefined}
      aria-hidden="true"
    />
  );
};

/**
 * FmModal — Lehti AI DS dialog. Replaces AntD `Modal`: portal to <body>, frosted
 * backdrop, Escape-to-close, body scroll lock while open, and an FmButton
 * ok/cancel footer (override via `footer`). No AntD.
 */
export const FmModal: React.FC<FmModalProps> = ({
  open,
  title,
  children,
  onOk,
  okText = 'OK',
  okDisabled = false,
  okLoading = false,
  onCancel,
  cancelText = 'Cancel',
  footer,
  dismissible = true,
  tone = 'default',
  presentation = 'overlay',
  size = 'md',
  width,
  className,
  'data-testid': dataTestId,
}) => {
  const resolvedWidth = resolveModalWidth(width, size);
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);
  const onCancelRef = React.useRef(onCancel);

  React.useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  const handleTabKey = (
    e: KeyboardEvent,
    dialogRef: React.RefObject<HTMLDivElement | null>,
  ): void => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusable = getFocusableElements(dialog);
    if (focusable.length === 0) {
      e.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleModalKeyDown = React.useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (hasVisibleChildOverlay(dialogRef.current?.parentElement ?? null)) return;
        if (dismissible) {
          onCancelRef.current?.();
        }
        return;
      }

      if (e.key !== 'Tab') return;
      handleTabKey(e, dialogRef);
    },
    [dismissible],
  );

  React.useEffect(() => {
    // An inline specimen surface owns no page-level behaviour: no scroll lock,
    // no focus trap, no Escape handler.
    if (!open || presentation === 'inline') return;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const onKey = (e: KeyboardEvent) => {
      handleModalKeyDown(e);
    };

    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFrame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const [firstFocusable] = getFocusableElements(dialog);
      const safeDestructiveChoice =
        tone === 'destructive'
          ? dialog.querySelector<HTMLElement>('[data-testid="fm-modal-cancel"]')
          : null;
      (safeDestructiveChoice ?? firstFocusable ?? dialog).focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus?.();
      previouslyFocusedRef.current = null;
    };
  }, [open, presentation, dismissible, handleModalKeyDown, tone]);

  if (!open) return null;

  const inline = presentation === 'inline';
  /* An inline surface is a specimen of a dialog, not a dialog: it traps nothing
     and dismisses nothing. Exposing `role="dialog"` would put a second
     same-named dialog in the accessibility tree beside the real one and make
     every by-role dialog lookup ambiguous. */
  const surfaceAttributes = dialogSurfaceAttributes({ inline, tone, title, titleId });

  const surface = (
    <FmOverlayPortalContext.Provider value={dialogRef}>
      <div className={surfaceAttributes.rootClassName} data-testid={dataTestId}>
        <FmModalBackdrop inline={inline} dismissible={dismissible} onCancel={onCancel} />
        <div
          ref={dialogRef}
          className={cx('fm-modal__dialog', className)}
          data-tone={surfaceAttributes.dataTone}
          role={surfaceAttributes.role}
          aria-modal={surfaceAttributes.ariaModal}
          aria-labelledby={surfaceAttributes.ariaLabelledBy}
          tabIndex={-1}
          style={{ width: resolvedWidth }}
        >
          <FmModalHeader
            title={title}
            titleId={titleId}
            dismissible={dismissible}
            onCancel={onCancel}
            cancelText={cancelText}
          />
          <div className="fm-modal__body">{children}</div>
          {footer === null ? null : (
            <div className="fm-modal__footer">
              {footer ?? (
                <FmModalDefaultFooter
                  onOk={onOk}
                  okText={okText}
                  okDisabled={okDisabled}
                  okLoading={okLoading}
                  onCancel={onCancel}
                  cancelText={cancelText}
                  tone={tone}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </FmOverlayPortalContext.Provider>
  );

  return inline ? surface : createPortal(surface, document.body);
};

const FmModalHeader: React.FC<{
  title?: React.ReactNode;
  titleId: string;
  dismissible: boolean;
  onCancel: () => void;
  cancelText: React.ReactNode;
}> = ({ title, titleId, dismissible, onCancel, cancelText }) => {
  if (!title) return null;
  return (
    <div className="fm-modal__header">
      <h2 className="fm-modal__title" id={titleId}>
        {title}
      </h2>
      {dismissible ? (
        <button
          type="button"
          className="fm-modal__close"
          onClick={onCancel}
          aria-label={typeof cancelText === 'string' ? cancelText : 'Close'}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
};

export default FmModal;
